import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Popover } from "components/Popover"

import { AutocompleteCheckList } from "../AutocompleteCheckList/AutocompleteCheckList"
import { CheckList } from "../CheckList/CheckList"
import { DatePickerFilter } from "../DatePickerFilter/DatePickerFilter"
import { FreeSearch } from "../FreeSearch/FreeSearch"
import { SelectFilter } from "../SelectFilter/SelectFilter"
import { Trigger } from "../Trigger/Trigger"
import { TriggerState } from "../Trigger/types"

import { FilterPopoverProps, FilterType } from "./types"

export const FilterPopover = ({ options, selectedItems, onChange, label, labelParts, filterType, placeholder, hideIndicator, dataTestId }: FilterPopoverProps) => {
    const filterPopoverRef = useRef<HTMLDivElement>(null)
    const [isOpen, setIsOpen] = useState(false)


    const handleApply = useCallback((value: string) => {
        onChange(value)
        setIsOpen(false)
    }, [onChange])

    const handleSelectChange = useCallback((value: string) => {
        onChange(value)
    }, [onChange])


    const getWidthAndHeight = useCallback(() => {
        switch (filterType) {
            case FilterType.CHECKLIST:
                return { width: '300px', height: '300px' }
            case FilterType.AUTOCOMPLETE_CHECKLIST:
                return { width: '300px', height: 'auto' }
            default:
                return {}
        }
    }, [filterType])

    const showCount = useMemo(() => {
        return selectedItems && selectedItems.length > 0 && (filterType === FilterType.CHECKLIST || filterType === FilterType.AUTOCOMPLETE_CHECKLIST)
    }, [selectedItems, filterType])

    useEffect(() => {
        if (!isOpen || filterType === FilterType.DATE_PICKER || filterType === FilterType.SELECT) return

        const listener = (event: MouseEvent) => {
            if (filterPopoverRef.current?.contains(event.target as Node)) {
                return
            }
            setIsOpen(false)
        }

        const timeoutId = setTimeout(() => {
            document.addEventListener('click', listener)
        }, 0)

        return () => {
            clearTimeout(timeoutId)
            document.removeEventListener('click', listener)
        }
    }, [isOpen, filterType])

    const renderContent = () => {
        switch (filterType) {
            case FilterType.CHECKLIST:
                return <CheckList items={options ?? []} selectedItems={selectedItems ?? []} onChange={onChange} />
            case FilterType.AUTOCOMPLETE_CHECKLIST:
                return <AutocompleteCheckList items={options ?? []} selectedItems={selectedItems ?? []} onChange={onChange} placeholder={placeholder} dataTestId={dataTestId} />
            case FilterType.FREE_SEARCH:
                return <FreeSearch label={label} onApply={handleApply} initialValue={selectedItems?.[0] || ''} />
            case FilterType.DATE_PICKER:
                return <DatePickerFilter label={label} onApply={handleApply} initialValue={selectedItems?.[0] || ''} />
            case FilterType.SELECT:
                return <SelectFilter items={options ?? []} selectedItems={selectedItems ?? []} onChange={handleSelectChange} />
            default:
                return null
        }
    }


    return (
        <div ref={filterPopoverRef} className="relative" data-testid={dataTestId}>
            <Popover {...getWidthAndHeight()} isOpen={isOpen} onClose={() => setIsOpen(false)} trigger={<Trigger showCount={showCount} onClick={() => setIsOpen(true)} count={selectedItems?.length ?? 0} state={selectedItems && selectedItems.length > 0 ? TriggerState.FILLED : TriggerState.EMPTY} label={label} labelParts={labelParts} isActive={isOpen} hideIndicator={hideIndicator} />}>
                {renderContent()}
            </Popover>
        </div>
    )
}